import React, { useEffect } from 'react';
import { View } from 'react-native';
import { Tabs, useRouter } from 'expo-router';
import { Home, LayoutGrid, FileSearch, MessageCircle, Briefcase } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { usePhoneShell } from '@/lib/useShell';
import { track } from '@/lib/analytics';
import { useAuth, useInvestorProfile } from '@/lib/supabase';
import { useDealflowFromSupabase, mergeServerDealflow } from '@/lib/supabase/investor-queries';
import { useTrackRecordReconciliation } from '@/lib/investor/track-record-reconcile';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useUserRole } from '@/lib/auth/role';
import { resolveGuardState } from '@/lib/auth/guard-state';
import { useAALGate } from '@/lib/auth/aal';
import { AuthGuardError } from '@/components/AuthGuardError';
import { mode } from '@/lib/config/app-mode';
import { COMMERCE_ENABLED } from '@/lib/commerce/policy';
import { useUnreadMessageCount } from '@/lib/messaging/use-unread-messages';

export default function InvestorTabLayout() {
  const { user, isLoading: isAuthLoading } = useAuth();
  // Persisted store used only in mock mode; production reads server-fetched profiles.role
  // via useUserRole. This prevents devtools-edits of localStorage from unlocking the
  // investor shell (SEC-FE-02 / RM-T1-18).
  const persistedRole = useProfileStore((s) => s.userRole);
  const { data: serverRole, isLoading: isRoleLoading, isError: isRoleError, refetch: refetchRole } = useUserRole(user?.id);
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();

  // ── Production dealflow sync (the "missing pipe") ──
  // The dealflow store starts EMPTY in production; the store's rehydration
  // comment always documented that this layout populates it via
  // useDealflowFromSupabase(), but the hook was never called anywhere.
  // dealflow.investor_id is an investor_profiles.id (NOT the auth user id),
  // so resolve the profile row first. Both hooks are disabled in
  // demo/sample mode — mock data stays untouched.
  const { data: investorProfileRow } = useInvestorProfile(
    mode.showSampleData ? undefined : user?.id,
  );
  const { data: serverDealflow } = useDealflowFromSupabase(investorProfileRow?.id);
  useEffect(() => {
    if (mode.showSampleData || !serverDealflow) return;
    // THE SERVER IS THE PIPELINE — nothing local survives the merge. This used
    // to preserve locally analysed pitch-deck items, which was necessary while
    // an analysed deck had no server identity. `investor-analyze-deck` now
    // writes the dealflow row itself (#412), so a local-only item means the
    // write failed and preserving it would hide that.
    const { dealflow, setDealflow } = useDealflowStore.getState();
    setDealflow(mergeServerDealflow(serverDealflow, dealflow));
  }, [serverDealflow]);

  // ── Track-record reconciliation ──
  // Lifts investments that exist only in this device's storage up to
  // `investor_track_record`. Onboarding used to persist just the COUNT, so the
  // records lived on whichever device typed them — the cause of "my portfolio
  // differs on mobile vs web". Self-healing and idempotent; see
  // lib/investor/track-record-reconcile.ts.
  useTrackRecordReconciliation(mode.showSampleData ? undefined : user?.id);

  // Wait for auth + server role fetch to finish before checking authorization to avoid
  // navigating before the Root Layout mounts (Expo Router race condition).
  const isLoading = isAuthLoading || isRoleLoading;
  const hasAuth = !!user || mode.isMock;
  const effectiveRole = mode.isMock ? persistedRole : serverRole;
  const isAuthorized = hasAuth && effectiveRole === 'investor';
  // AAL2 boundary (SEC-AUTH-02c): password login mints a REAL AAL1 session
  // before MFA is verified, so role alone is not enough — an enrolled user who
  // skipped /mfa-verify was previously inside the app on an AAL1 token. Users
  // with no MFA factor report 'satisfied' and are unaffected.
  const { status: aal, retry: retryAal } = useAALGate();
  const guardState = resolveGuardState({ isLoading, isError: isRoleError, isAuthorized, aal });

  // Unread badge on the Messages tab. It belongs HERE rather than in the
  // messages screen because a badge earns its keep precisely when you are not
  // on that screen. Resolves its investor id from the profile store — the same
  // selector investor-messages uses — so both land on one React Query cache
  // entry instead of two that could disagree. Called before the early returns
  // below, per the rules of hooks.
  const unreadMessages = useUnreadMessageCount('investor');

  useEffect(() => {
    if (guardState === 'redirect') {
      // Authenticated but not entitled to THIS group → '/', which re-routes to
      // the group they do belong to. Sending a live session to /login bounces it
      // straight back and, where the router and the guard disagree about the
      // group, loops (see (admin)/_layout.tsx). Only signed-out visitors go to
      // /login.
      router.replace(hasAuth ? '/' : '/login');
    } else if (guardState === 'step-up-required') {
      // Enrolled but still AAL1. Send them to complete the step-up rather than
      // to /login — they have already proven their password, and bouncing them
      // to a fresh sign-in would just mint another AAL1 session and loop.
      router.replace('/mfa-verify');
    }
  }, [guardState, hasAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transient role-fetch error → offer a retry instead of bouncing the user out.
  // Retry must re-run BOTH probes. The AAL probe is a separate React Query
  // entry, so refetching only the role left a failed AAL result stale — the
  // guard kept deriving 'error' from it and the screen could never recover.
  if (guardState === 'error') {
    return <AuthGuardError onRetry={() => { refetchRole(); retryAal(); }} />;
  }
  if (guardState !== 'authorized') return <View style={{ flex: 1 }} />;

  return (
    <Tabs
      screenListeners={{
        tabPress: (e) => {
          track('tab_switch', { tab: e.target?.split('-')[0] ?? 'unknown', group: 'investor' });
        },
      }}
      screenOptions={{
        headerShown: false,
        // The tab bar is the navigation owner for every PHONE-SHAPED viewport —
        // native and phone web alike — and yields to the sidebar above the tablet
        // breakpoint. It used to key off `isWeb`, which hid it at every web width
        // while the sidebar only mounts at >= 768px, so phone web had no
        // navigation at all. Reusing this same navigator (rather than building a
        // parallel web nav) is what keeps `Tabs.Protected` gating, `href: null`
        // hiding and the unread badges identical on both platforms.
        tabBarStyle: phoneShell
          ? {
              backgroundColor: colors.surface.primary,
              borderTopColor: colors.border.light,
              borderTopWidth: 1,
              height: 85,
              paddingTop: 8,
            }
          : { display: 'none' },
        tabBarActiveTintColor: colors.primary[600],
        tabBarInactiveTintColor: colors.light[500],
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: '600',
          marginTop: 4,
        },
      }}
    >
      <Tabs.Screen
        name="investor-dashboard"
        options={{
          title: 'Home',
          tabBarIcon: ({ color, size }) => <Home size={size} color={color} />,
        }}
      />
      {/* Order mirrors the early-stage investment lifecycle, and now FOLLOWS it
         left to right: a deal enters the pipeline (Dealflow), is investigated
         (Due Diligence), is negotiated (Messages), and what you already own
         sits in Portfolio at the end. Portfolio previously sat second, which
         put the last stage of the lifecycle next to its first.

         INBOX IS NOT A TAB — it lives in the dashboard header beside the bell.
         Both are inboxes of unread things that arrive on their own, so they
         belong at the same level; Inbox is somewhere you go when a badge tells
         you to, whereas a tab is somewhere you browse. It keeps its badge and
         its prominent dashboard placement.

         PROFILE IS NOT A TAB either — it is the avatar at the top-right of the
         page header, the same control and the same position as the startup
         persona. Removing it freed the slot that the six-tab cap had been
         forcing a choice over. */}
      <Tabs.Screen
        name="dealflow"
        options={{
          title: 'Dealflow',
          tabBarIcon: ({ color, size }) => <LayoutGrid size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="duediligence"
        options={{
          title: 'Due Diligence',
          tabBarIcon: ({ color, size }) => <FileSearch size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="investor-messages"
        options={{
          title: 'Messages',
          tabBarIcon: ({ color, size }) => <MessageCircle size={size} color={color} />,
          // undefined, not 0 — React Navigation renders a badge for any defined
          // value, so 0 would pin an empty dot to the icon permanently.
          tabBarBadge: unreadMessages > 0 ? unreadMessages : undefined,
        }}
      />
      <Tabs.Screen
        name="portfolio"
        options={{
          title: 'Portfolio',
          tabBarIcon: ({ color, size }) => <Briefcase size={size} color={color} />,
        }}
      />
      {/* Hidden routes — href: null hides from tab bar on all platforms.
         Routes remain navigable via router.push(). */}
      <Tabs.Screen name="investorprofile" options={{ href: null }} />
      {/* Reached from the dashboard header icon, not the tab bar. */}
      <Tabs.Screen name="submissions" options={{ href: null }} />
      <Tabs.Screen name="assistant" options={{ href: null }} />
      <Tabs.Protected guard={COMMERCE_ENABLED}>
        <Tabs.Screen name="investor-subscription" options={{ href: null }} />
      </Tabs.Protected>
      <Tabs.Screen name="help" options={{ href: null }} />
      <Tabs.Screen name="duediligence/[id]" options={{ href: null }} />
      <Tabs.Screen name="investor-score-detail" options={{ href: null }} />
      {/* Where `monitoring` deals live (see stage.ts's `surfacesFor`). Reached
         via navigation (e.g. from a card's Monitor action), not a seventh
         tab — the bar is already at its practical cap. */}
      <Tabs.Screen name="watchlist" options={{ href: null }} />
      <Tabs.Screen name="activity" options={{ href: null }} />
    </Tabs>
  );
}
